// Production zod DTOs (BUILD_SPEC.md §5.5, §8.3, §9.4-9.5) — owned by G7. Consumed by
// services/api/src/modules/production/ (request validation via @hono/zod-validator)
// and, later, the web app (end-to-end types via z.infer, per S7).
import { z } from 'zod';
import { MOVEMENT_SOURCES, PRODUCTION_RUN_STATUSES } from '../enums.js';
import { moisturePctSchema, moneyCentsSchema, quantitySchema, ulidSchema } from './common.js';
export const RUN_MODES = ['SHIFT', 'BATCH'];
/** production_run_consumables.source — distinct from MOVEMENT_SOURCES (see
 * packages/db/src/schema/production.ts header for the full rationale). */
export const PRODUCTION_CONSUMABLE_SOURCES = ['BACKFLUSHED', 'ACTUAL'];
// ---------------------------------------------------------------------------
// Process stages
// ---------------------------------------------------------------------------
export const createProcessStageSchema = z.object({
    siteId: ulidSchema,
    code: z.string().min(1).max(40),
    name: z.string().min(1).max(200),
    runMode: z.enum(RUN_MODES),
    sortOrder: z.number().int().default(0),
});
export const updateProcessStageSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    sortOrder: z.number().int().optional(),
    isActive: z.boolean().optional(),
});
// ---------------------------------------------------------------------------
// Stage recipes
// ---------------------------------------------------------------------------
/** Informational planning proportion only — the run-POST cost math (§8.3/§9.4) is
 * always driven by the run's ACTUAL recorded inputs, never by this share. */
export const recipeInputLineSchema = z.object({
    productId: ulidSchema,
    share: z.number().nonnegative(),
});
export const recipeOutputLineSchema = z.object({
    productId: ulidSchema,
    splitPct: z.number().min(0).max(100).nullable().optional(),
    expectedMcPct: moisturePctSchema.nullable().optional(),
    isPrimary: z.boolean().default(false),
});
export const recipeConsumableLineSchema = z.object({
    productId: ulidSchema,
    qtyPerOutputTonne: quantitySchema,
});
export const createStageRecipeSchema = z.object({
    expectedYieldDryPct: z.number().min(0).max(100),
    inputs: z.array(recipeInputLineSchema).min(1),
    outputs: z.array(recipeOutputLineSchema).min(1),
    consumables: z.array(recipeConsumableLineSchema).default([]),
    effectiveFrom: z.coerce.date(),
});
// ---------------------------------------------------------------------------
// Production runs — open / lines / close
// ---------------------------------------------------------------------------
export const openProductionRunSchema = z.object({
    siteId: ulidSchema,
    stageId: ulidSchema,
    shiftLabel: z.string().max(100).nullable().optional(),
    startedAt: z.coerce.date().optional(),
    notes: z.string().max(2000).nullable().optional(),
});
// Line quantities are always operator-entered MAGNITUDES (never signed) — the run
// handler (services/api/src/modules/production/runs.ts) decides the ledger-facing
// sign (inputs/consumables relieve their from-location => negative; outputs receive
// into their to-location => positive) when it calls recordMovement at POST.
const nonNegativeQtySchema = quantitySchema.refine((v) => Number(v) >= 0, 'qty must be non-negative');
const nonNegativeHandlingUnitsSchema = z.number().int().nonnegative();
export const runInputLineSchema = z.object({
    productId: ulidSchema,
    fromLocationId: ulidSchema,
    qtyKg: nonNegativeQtySchema,
    mcPct: moisturePctSchema.nullable().optional(),
    source: z.enum(MOVEMENT_SOURCES),
    handlingUnits: nonNegativeHandlingUnitsSchema.nullable().optional(),
});
export const updateRunInputLineSchema = runInputLineSchema.partial();
export const runOutputLineSchema = z.object({
    productId: ulidSchema,
    toLocationId: ulidSchema,
    qtyKg: nonNegativeQtySchema,
    mcPct: moisturePctSchema.nullable().optional(),
    handlingUnits: nonNegativeHandlingUnitsSchema.nullable().optional(),
    isPrimary: z.boolean().default(false),
});
export const updateRunOutputLineSchema = runOutputLineSchema.partial();
export const runConsumableLineSchema = z.object({
    productId: ulidSchema,
    fromLocationId: ulidSchema,
    qty: nonNegativeQtySchema,
    source: z.enum(PRODUCTION_CONSUMABLE_SOURCES).default('ACTUAL'),
});
export const updateRunConsumableLineSchema = runConsumableLineSchema.partial();
export const closeProductionRunSchema = z.object({
    endedAt: z.coerce.date().optional(),
    downtimeMinutes: z.number().int().min(0).nullable().optional(),
    /** Every recipe consumable line without a matching consumable already recorded on
     * the run gets a BACKFLUSHED default line at CLOSE (qtyPerOutputTonne x total output
     * tonnes) — all booked from this one location. Required only when the pinned recipe
     * actually has consumable lines to backflush; validated in the handler, not here,
     * since that depends on the recipe (not visible to this schema). */
    consumablesFromLocationId: ulidSchema.nullable().optional(),
});
// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------
export const productionRunStatusSchema = z.enum(PRODUCTION_RUN_STATUSES);
export const postProductionRunResponseSchema = z.object({
    runId: ulidSchema,
    status: z.literal('POSTED'),
    runCostCents: moneyCentsSchema,
    // NULL means "no KG dry-mass basis to compute a yield from" — a zero-KG-output
    // or all-EACH run (see services/api modules/production/runs.ts's /post handler,
    // which preserves the null rather than coercing to 0 and misreporting a
    // genuinely unknown yield as 0%). Contract fix, gate round 2026-07-07: these
    // previously claimed plain z.number() while the runtime returned null.
    yieldDryPctActual: z.number().nullable(),
    yieldVariancePct: z.number().nullable(),
    summary: z.string(),
});
export const reverseProductionRunResponseSchema = z.object({
    runId: ulidSchema,
    status: z.literal('REVERSED'),
    reversedMovementCount: z.number().int(),
    summary: z.string(),
});
//# sourceMappingURL=production.js.map